/* eslint-disable no-console */
import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react'
import useApiRequest from 'ui/utilities/useApiRequest'
import { PROGRAM_SERVICE, GENERAL_SETTINGS, POTATO_TYPES_ALLOCATION } from 'helpers/constants'
import { useOktaAuth } from '@okta/okta-react'
import { showErrorToast } from 'ui/components/Toast/toast'
import { useAppSelector } from 'store'
import { formatProgramName } from 'common/string-constants'

// --- Cookie helpers ---
const isSecure = typeof window !== 'undefined' && window.location?.protocol === 'https:'
export const COOKIE_NAME = 'gspt_selectedProgram' // keep same name as localStorage key for familiarity

function setCookie(name, valueObj, days = 365) {
  try {
    const expires = new Date()
    expires.setDate(expires.getDate() + days)
    const encoded = encodeURIComponent(JSON.stringify(valueObj))
    const parts = [
      `${name}=${encoded}`,
      `expires=${expires.toUTCString()}`,
      'path=/', // cookie available across the app
      'SameSite=Lax',
      isSecure ? 'Secure' : null, // add Secure on HTTPS
    ].filter(Boolean)
    document.cookie = parts.join('; ')
  } catch (e) {
    console.log('Facing issue while setting up cookie', e)
    // swallow cookie errors silently to avoid breaking UX
    // console.warn('Failed to set cookie', e)
  }
}

export function getCookie(name) {
  if (typeof document === 'undefined') return null
  const match = document.cookie?.split('; ')?.find((row) => row.startsWith(`${name}=`))
  if (!match) return null
  try {
    const val = decodeURIComponent(match.split('=')[1])
    return JSON.parse(val)
  } catch {
    return null
  }
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`
}

// Create context for Program
const ProgramContext = createContext()

// Provider component
export function ProgramProvider({ children }) {
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(false)
  const { getData } = useApiRequest()
  const { authState } = useOktaAuth()
  const [generalSettings, setGeneralSettings] = useState(null)
  const [potatoTypeHierarchy, setPotatoTypeHierarchy] = useState([])
  const userDetail = useAppSelector((state) => state.userDetails)

  const selectedProgramId = useMemo(() => selectedProgram?.id || null, [selectedProgram])
  const selectedProgramName = useMemo(() => selectedProgram?.displayText || null, [selectedProgram])
  const hasPrograms = useMemo(() => programs.length > 0, [programs])

  // Update selected program (keeps cookie + localStorage in sync)
  const updateSelectedProgram = useCallback((program) => {
    if (program && program?.id) {
      setSelectedProgram(program)
      const consent = localStorage.getItem('cookieConsent')
      console.log('updateSelectedProgram consent', consent)
      if (consent && consent === 'accepted') {
        // localStorage.setItem(COOKIE_NAME, JSON.stringify(program))
        setCookie(COOKIE_NAME, program, 365) // 1 year
      }
      if (consent && consent === 'declined') {
        deleteCookie(COOKIE_NAME)
      }
    } else {
      setSelectedProgram(null)
    }
  }, [])

  // Fetch all programs
  const fetchPrograms = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getData(`${PROGRAM_SERVICE}`)

      if (response?.status === 500) {
        showErrorToast(response?.message || 'Failed to fetch programs')
        return
      }

      if (response && response?.length > 0) {
        const programList = response.map((item) => ({
          displayText: formatProgramName(item?.programname),
          id: item.programid,
          value: item.programid,
          isBadge: item.active,
          originalData: item,
        }))

        setPrograms(programList)

        // Set default program if not already selected
        if (!selectedProgram) {
          // Prefer persisted cookie/localStorage; else fallback to user's default
          const cookieProgram = getCookie(COOKIE_NAME)
          if (cookieProgram?.id && programList?.some((p) => p.id === cookieProgram?.id)) {
            updateSelectedProgram(cookieProgram)
          } else {
            const userDetails = JSON.parse(localStorage.getItem('app_user_details') || '{}')
            const defaultProgram = programList?.find(
              (item) => item.id === userDetails?.appUser?.default_program_id
            )
            if (defaultProgram) {
              updateSelectedProgram(defaultProgram)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching programs:', error)
      showErrorToast('Failed to load programs. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [selectedProgram, updateSelectedProgram])

  // Fetch programs for specific user (used in some pages)
  const fetchProgramsForUser = useCallback(
    async (userId) => {
      if (!userId) {
        console.warn('No userId provided for fetchProgramsForUser')
        return
      }

      setLoading(true)
      try {
        const response = await getData(`${PROGRAM_SERVICE}/v1/${userId}`)

        if (response?.status === 500) {
          showErrorToast(response?.message || 'Failed to fetch user programs')
          return
        }

        if (response?.programs && response.programs.length > 0) {
          const programList = response.programs.map(({ programId, programName, ...props }) => ({
            displayText: programName,
            id: programId,
            value: programId,
            ...props,
            programName,
            programId,
            originalData: { programId, programName, ...props },
          }))

          setPrograms(programList)

          // Set default program if not already selected
          if (!selectedProgram && programList.length > 0) {
            updateSelectedProgram(programList[0])
          }
        }
      } catch (error) {
        console.error('Error fetching user programs:', error)
        showErrorToast('Failed to load user programs. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [selectedProgram]
  )

  // Fetch programs with status filter (used in some pages)
  const fetchProgramsWithStatus = useCallback(
    async (statuses = ['Active', 'Created']) => {
      setLoading(true)
      try {
        const statusParams = statuses.map((status) => `status=${status}`).join('&')
        const response = await getData(`${PROGRAM_SERVICE}?${statusParams}&filter=true`)

        if (response?.status === 500) {
          showErrorToast(response?.message || 'Failed to fetch programs')
          return []
        }

        if (response && response?.length > 0) {
          const programList = response.map((item) => ({
            displayText: formatProgramName(item?.programname),
            id: item.programid,
            value: item.programid,
            isBadge: item.active,
            originalData: item,
          }))

          return programList
        }
        return []
      } catch (error) {
        console.error('Error fetching programs with status:', error)
        showErrorToast('Failed to load programs. Please try again.')
        return []
      } finally {
        setLoading(false)
      }
    },
    [selectedProgram, updateSelectedProgram]
  )

  // Initialize from cookie/localStorage on mount
  useEffect(() => {
    const storedProgram = localStorage.getItem(COOKIE_NAME)
    const cookieProgram = getCookie(COOKIE_NAME)

    // Prefer cookie (authoritative), fall back to localStorage
    const initial =
      cookieProgram ||
      (storedProgram
        ? (() => {
            try {
              return JSON.parse(storedProgram)
            } catch {
              return null
            }
          })()
        : null)

    if (initial?.id) {
      setSelectedProgram(initial) // don't call updateSelectedProgram here to avoid rewrite
    }
  }, [])

  // Keep cookie in sync if some code path directly updates state (defensive)
  useEffect(() => {
    if (selectedProgram?.id) {
      if (getCookie(COOKIE_NAME) !== null) {
        setCookie(COOKIE_NAME, selectedProgram, 365)
      }
      // localStorage.setItem(COOKIE_NAME, JSON.stringify(selectedProgram))
    }
  }, [selectedProgram])

  // Fetches general settings for the given (or currently selected) program id
  const fetchGeneralSettings = useCallback(
    async (programIdOverride) => {
      if (!authState || authState.isPending || !authState.isAuthenticated) {
        return
      }
      const programId = programIdOverride ?? selectedProgram?.id
      if (!programId) {
        setGeneralSettings(null)
        return
      }
      try {
        const settings = await getData(`${GENERAL_SETTINGS}/${programId}`)
        if (settings?.status && settings?.message) {
          setGeneralSettings(null)
          return
        }
        setGeneralSettings(settings || null)
      } catch (e) {
        setGeneralSettings(null)
      }
    },
    [authState, getData, selectedProgram]
  )
  useEffect(() => {
    fetchGeneralSettings()
  }, [selectedProgram, authState?.isAuthenticated])

  // Exposed so pages that update general settings (e.g. MasterDataPage/GeneralSettings)
  // can refresh this shared context value without affecting unrelated refreshData listeners
  const refetchGeneralSettings = useCallback(
    () => fetchGeneralSettings(selectedProgram?.id),
    [fetchGeneralSettings, selectedProgram]
  )

  // Fetch potato type hierarchy whenever the selected program changes
  useEffect(() => {
    const fetchPotatoTypeHierarchy = async () => {
      if (!authState || authState.isPending || !authState.isAuthenticated) {
        return
      }
      const programId = selectedProgram?.id
      if (!programId) {
        setPotatoTypeHierarchy([])
        return
      }
      try {
        const response = await getData(
          `${POTATO_TYPES_ALLOCATION}/${programId}/potato-types/hierarchy`
        )

        if (response?.status && response?.message) {
          console.error('Error fetching potato type allocation hierarchy:', response.message)
          setPotatoTypeHierarchy([])
          return
        }

        if (
          response?.data?.potatoTypeHierarchy &&
          Array.isArray(response.data.potatoTypeHierarchy)
        ) {
          setPotatoTypeHierarchy(response.data.potatoTypeHierarchy)
        } else {
          setPotatoTypeHierarchy([])
        }
      } catch (error) {
        console.error('Error fetching potato type allocation hierarchy:', error)
        setPotatoTypeHierarchy([])
      }
    }
    fetchPotatoTypeHierarchy()
  }, [selectedProgram, authState?.isAuthenticated])

  useEffect(() => {
    if (userDetail?.makeProgramApiCall && userDetail?.isLoggedIn && userDetail?.appUser) {
      fetchPrograms()
    }
  }, [userDetail])

  // if no program is selected, set the first program
  useEffect(() => {
    if (!selectedProgram && programs.length > 0) {
      updateSelectedProgram(programs?.[0])
    }
  }, [programs, selectedProgram, updateSelectedProgram])

  /**
   * Resolves weight UoM (code + id) and area UoM id from generalSettings
   * for a given potato type name (lowercase).
   * @param {string} potatoTypeName — e.g. "seed potato", "chipstock", "minituber", "plantlet"
   * @returns {{ weightUomCode: string, weightUomId: number|null, areaUomId: number|null }}
   */
  const getUomByPotatoType = useCallback(
    (potatoTypeName) => {
      const name = (potatoTypeName || '').toLowerCase()
      if (!name || !generalSettings) {
        return { weightUomCode: '-', weightUomId: null, areaUomId: null }
      }

      const settingsMap = [
        { match: 'seed', weight: 'seedWeightSettings', area: 'seedAreaSettings' },
        { match: 'chipstock', weight: 'chipStockWeightSettings', area: 'chipStockAreaSettings' },
        { match: 'minituber', weight: 'minituberWeightSettings', area: null },
        { match: 'plantlet', weight: 'plantletWeightSettings', area: null },
      ]

      const entry = settingsMap.find((e) => name.includes(e.match))
      if (!entry) return { weightUomCode: '-', weightUomId: null, areaUomId: null }

      const weightSettings = generalSettings[entry.weight]
      const areaSettings = entry.area
        ? generalSettings[entry.area]
        : generalSettings.defaultAreaUnit

      return {
        weightUomCode: weightSettings?.uomCode || '-',
        weightUomId: weightSettings?.uomId || null,
        areaUomId: areaSettings?.uomId || areaSettings?.measurementId || null,
      }
    },
    [generalSettings]
  )

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = useMemo(
    () => ({
      // State
      selectedProgram,
      programs,
      loading,
      generalSettings,
      potatoTypeHierarchy,

      // Actions
      setSelectedProgram: updateSelectedProgram, // exposes the cookie-aware setter
      fetchPrograms,
      fetchProgramsForUser,
      fetchProgramsWithStatus,
      refetchGeneralSettings,

      // Computed
      selectedProgramId,
      selectedProgramName,
      hasPrograms,

      // Helpers
      getUomByPotatoType,
    }),
    [
      selectedProgram,
      programs,
      loading,
      generalSettings,
      potatoTypeHierarchy,
      selectedProgramId,
      selectedProgramName,
      hasPrograms,
      getUomByPotatoType,
      refetchGeneralSettings,
    ]
  )

  return <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>
}

// Hook to use the Program context
export function useProgram() {
  const context = useContext(ProgramContext)
  if (!context) {
    throw new Error('useProgram must be used within a ProgramProvider')
  }
  return context
}

export { ProgramContext }
